// src/supabaseClient.js
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Verificação das variáveis de ambiente
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('❌ REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY devem ser definidas nas variáveis de ambiente');
}

// SINGLETON PATTERN - Garante uma única instância
let supabaseInstance = null;
let lastActivityTimestamp = Date.now();
let sessionCheckInProgress = false;
let sessionCheckTimeout = null;

// Cache para armazenar resultado da última verificação de sessão
let lastSessionCheck = {
  timestamp: 0,
  hasValidSession: false
};

// Função para atualizar o timestamp de atividade
const updateActivityTimestamp = () => {
  lastActivityTimestamp = Date.now();
};

// Verificar se a conexão precisa ser reestabelecida (aumentado para 30 minutos)
const shouldReconnect = () => {
  // Se passaram mais de 30 minutos sem atividade
  return Date.now() - lastActivityTimestamp > 30 * 60 * 1000;
};

const createSupabaseClient = () => {
  // Se já existe uma instância e não precisamos reconectar, usar a existente
  if (supabaseInstance && !shouldReconnect()) {
    updateActivityTimestamp();
    return supabaseInstance;
  }

  // Se existir uma instância mas precisamos reconectar
  if (supabaseInstance && shouldReconnect()) {
    console.log('🔄 Reconectando ao Supabase após inatividade prolongada');
    // Apenas redefinimos timestamp em vez de recriar a instância
    updateActivityTimestamp();
    return supabaseInstance;
  }

  // Se não temos instância, criar uma nova
  if (!supabaseInstance) {
    console.log('🔧 Criando nova instância do Supabase Client');
    
    // Configuração do cliente Supabase
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`,
      },
      global: {
        headers: {
          'x-client-info': 'react-app-singleton'
        }
      },
      db: {
        schema: 'public'
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    });

    // Log de controle da instância
    supabaseInstance.auth.onAuthStateChange((event, session) => {
      console.log(`🔐 Auth State Change: ${event}`, session?.user?.email || 'No user');
      updateActivityTimestamp();
      
      // Atualizar cache de sessão quando o estado de autenticação mudar
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        lastSessionCheck = {
          timestamp: Date.now(),
          hasValidSession: true
        };
      } else if (event === 'SIGNED_OUT') {
        lastSessionCheck = {
          timestamp: Date.now(),
          hasValidSession: false
        };
      }
    });
  }

  updateActivityTimestamp();
  return supabaseInstance;
};

// Verificar sessão de forma não bloqueante com timeout
const verifySession = async () => {
  // Se já estamos verificando, não iniciar outra verificação
  if (sessionCheckInProgress) return;
  
  // Se verificamos recentemente (últimos 30 segundos), usar resultado em cache
  const SESSION_CACHE_TTL = 30 * 1000; // 30 segundos
  if (Date.now() - lastSessionCheck.timestamp < SESSION_CACHE_TTL) {
    console.log('✅ Usando verificação de sessão em cache');
    return lastSessionCheck.hasValidSession;
  }
  
  sessionCheckInProgress = true;
  
  try {
    // Definir um timeout para a verificação de sessão
    const sessionPromise = supabaseInstance.auth.getSession();
    
    // Criar uma promessa com timeout
    const timeoutPromise = new Promise((_, reject) => {
      sessionCheckTimeout = setTimeout(() => {
        reject(new Error('Timeout ao verificar sessão'));
      }, 5000); // 5 segundos de timeout
    });
    
    // Usar Promise.race para garantir que não ficará bloqueado indefinidamente
    const { data } = await Promise.race([sessionPromise, timeoutPromise]);
    
    // Limpar o timeout se a promessa resolver antes
    clearTimeout(sessionCheckTimeout);
    
    // Atualizar o cache de sessão
    const hasValidSession = !!data?.session;
    lastSessionCheck = {
      timestamp: Date.now(),
      hasValidSession
    };
    
    if (hasValidSession) {
      console.log('✅ Sessão verificada com sucesso');
    } else {
      console.log('⚠️ Sessão inválida ou expirada');
    }
    
    return hasValidSession;
  } catch (error) {
    console.warn('⚠️ Erro ao verificar sessão:', error.message);
    // Se houve erro, assumimos que a sessão ainda é válida para não interromper o usuário
    return true;
  } finally {
    clearTimeout(sessionCheckTimeout);
    sessionCheckInProgress = false;
  }
};

// Adicionar listener de visibilidade de forma otimizada
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      updateActivityTimestamp();
      
      // Se a última verificação foi há mais de 30 segundos, verificar novamente
      if (Date.now() - lastSessionCheck.timestamp > 30 * 1000) {
        // Verificar sessão de forma não bloqueante com delay para não bloquear a UI
        setTimeout(() => {
          verifySession().then(isValid => {
            console.log('✅ Sessão verificada após retorno à aba');
          }).catch(err => {
            // Apenas log, sem afetar a experiência do usuário
            console.warn('⚠️ Erro ao verificar sessão:', err.message);
          });
        }, 100); // Pequeno delay para garantir que a UI seja renderizada primeiro
      } else {
        console.log('✅ Sessão já verificada recentemente');
      }
    }
  });
}

// Ping suave ao Supabase para manter a conexão ativa
const pingSupabase = async () => {
  // Só fazer ping se a aba estiver visível
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    try {
      // Usar uma query leve que não bloqueie a UI
      const { error } = await supabaseInstance
        .from('profiles')
        .select('count(*)', { count: 'exact', head: true })
        .limit(1)
        .maybeSingle();
        
      if (!error) {
        updateActivityTimestamp();
      }
    } catch (error) {
      // Apenas logar o erro sem afetar a experiência do usuário
      console.warn('⚠️ Ping ao Supabase falhou:', error.message);
    }
  }
};

// Inicializar instância do Supabase
const supabase = createSupabaseClient();

// Executar ping a cada 5 minutos para manter a conexão ativa
setInterval(pingSupabase, 5 * 60 * 1000);

// Função para reset manual (usar apenas em desenvolvimento)
export const resetSupabaseInstance = () => {
  if (process.env.NODE_ENV === 'development') {
    // Apenas redefinir timestamp em vez de recriar a instância
    updateActivityTimestamp();
    console.log('🔄 Timestamp do Supabase resetado (desenvolvimento)');
  }
};

// Função para verificar saúde da conexão
export const checkConnection = async () => {
  updateActivityTimestamp();
  return await testConnection();
};

// Função para testar a conexão com timeout
export const testConnection = async () => {
  try {
    // Criar uma promessa com timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Timeout ao testar conexão'));
      }, 5000); // 5 segundos de timeout
    });
    
    // Executar a query com timeout
    const queryPromise = supabase
      .from('profiles')
      .select('count(*)', { count: 'exact' })
      .limit(1);
      
    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
    
    if (error) throw error;
    
    console.log('✅ Conexão com Supabase estabelecida com sucesso!');
    updateActivityTimestamp();
    return { success: true, message: 'Conexão bem-sucedida' };
  } catch (error) {
    console.error('❌ Erro ao testar conexão com Supabase:', error.message);
    return { 
      success: false, 
      message: 'Falha na conexão', 
      error: error.message || 'Erro desconhecido' 
    };
  }
};

export default supabase;